import { Config } from 'config/envConfig';
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
    Button,
    ConfirmModal,
    alert,
    AlertType,
    LoadingView,
} from '@quyva/shared-ui';
import {
    faFloppyDisk,
    faPlus,
    faCircleInfo,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getUrl } from '../../../pdfDisplay/utils';
import { PromptListView } from './promptListView';
import { Operation } from '@quyva/common';

const normalizeProcessName = (name) =>
    String(name || '')
        .trim()
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\s+/g, ' ');

const normalizePromptName = (name) => (name || '').trim();
const normalizePromptKey = (name) => normalizePromptName(name).toLowerCase();

const promptDomId = (processName, promptName) => {
    const safe = normalizePromptName(promptName)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
    return `prompt-${String(processName || 'unknown')
        .toLowerCase()
        .replace(/\s+/g, '-')}-${safe || 'unnamed'}`;
};

const getPersonaPromptNames = (processesList) => {
    const persona = (processesList || []).find(
        (p) =>
            normalizeProcessName(p?.process_name) ===
            normalizeProcessName(Operation.PERSONA_PROMPTS)
    );
    return (persona?.sub_tasks || []).map((t) => t?.prompt).filter(Boolean);
};

const getDuplicatePersonaKeys = (processesList) => {
    const names = getPersonaPromptNames(processesList);
    const counts = new Map();
    for (const name of names) {
        const key = normalizePromptKey(name);
        if (!key) continue;
        counts.set(key, (counts.get(key) || 0) + 1);
    }
    const dupes = new Set();
    for (const [k, c] of counts.entries()) {
        if (c > 1) dupes.add(k);
    }
    return dupes;
};

const hasDuplicatePersonaPrompts = (processesList) => {
    const names = getPersonaPromptNames(processesList);
    const seen = new Set();
    for (const name of names) {
        const key = normalizePromptKey(name);
        if (!key) continue;
        if (seen.has(key)) return true;
        seen.add(key);
    }
    return false;
};

const hasInvalidPersonaPrompts = (processesList) => {
    const persona = (processesList || []).find(
        (p) =>
            normalizeProcessName(p?.process_name) ===
            normalizeProcessName(Operation.PERSONA_PROMPTS)
    );
    const tasks = persona?.sub_tasks || [];
    for (const t of tasks) {
        const name = normalizePromptName(t?.prompt);
        const instr = (t?.instruction || '').trim();
        if (!name) return true;
        if (!instr) return true;
    }
    return false;
};

const ensurePersonaUiIds = (processesList) => {
    let changed = false;
    const next = (processesList || []).map((p) => {
        if (
            normalizeProcessName(p?.process_name) !==
            normalizeProcessName(Operation.PERSONA_PROMPTS)
        ) {
            return p;
        }
        let tasksChanged = false;
        const nextTasks = (p.sub_tasks || []).map((t) => {
            if (t && !t._uiId) {
                changed = true;
                tasksChanged = true;
                return {
                    ...t,
                    _uiId: `ui-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                };
            }
            return t;
        });
        if (!tasksChanged) return p;
        return { ...p, sub_tasks: nextTasks };
    });
    return changed ? next : processesList;
};

const formatProcessName = (processName) => {
    return (processName || '')
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
};

const buildCopyName = (baseName, existingNames) => {
    const base = normalizePromptName(baseName);
    const existing = new Set(existingNames.map((n) => normalizePromptKey(n)));

    const candidate0 = `${base} (COPY)`;
    if (!existing.has(normalizePromptKey(candidate0))) return candidate0;

    let i = 1;
    while (i < 1000) {
        const candidate = `${base} (COPY${i})`;
        if (!existing.has(normalizePromptKey(candidate))) return candidate;
        i++;
    }

    return `${base} (COPY${Date.now()})`;
};

export const PersonaPromptsPanel = ({ isAdmin = false, repoid, onDirtyChange, onSaved }) => {
    const user = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('current'));
        } catch (e) {
            return null;
        }
    }, []);

    const [processes, setProcesses] = useState([]);
    const [originalProcesses, setOriginalProcesses] = useState([]);
    const [selectedPrompt, setSelectedPrompt] = useState(null);
    const [expandPromptId, setExpandPromptId] = useState(null);
    const [scrollToPromptId, setScrollToPromptId] = useState(null);

    const [pendingPersonaPromptDeletes, setPendingPersonaPromptDeletes] =
        useState([]);
    const [confirmPersonaDeleteOpen, setConfirmPersonaDeleteOpen] =
        useState(false);
    const [pendingPersonaDeleteTarget, setPendingPersonaDeleteTarget] =
        useState(null);

    const [isSavingPersonaPrompts, setIsSavingPersonaPrompts] = useState(false);

    const selectedProcess = Operation.PERSONA_PROMPTS;

    const hasChanges =
        JSON.stringify(processes) !== JSON.stringify(originalProcesses) ||
        pendingPersonaPromptDeletes.length > 0;

    useEffect(() => {
        onDirtyChange?.(!!hasChanges);
    }, [hasChanges, onDirtyChange]);

    const currentProcess = useMemo(() => {
        return (processes || []).find(
            (p) => normalizeProcessName(p?.process_name) === normalizeProcessName(selectedProcess)
        );
    }, [processes]);

    const fetchProcesses = async () => {
        try {
            if (!user?.email || !repoid) return;

            let response;
            try {
                response = await axios.get(
                    getUrl(
                        `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/prompts_routes/persona-prompts-for-repo?email=${encodeURIComponent(
                            user.email
                        )}&repoid=${encodeURIComponent(repoid)}`
                    )
                );
            } catch (e) {
                console.error(
                    'Error fetching persona prompts for repo:',
                    e?.response?.data || e
                );
                return;
            }

            const payload = response?.data;

            const system = Array.isArray(payload?.system) ? payload.system : [];
            const users = Array.isArray(payload?.users) ? payload.users : [];

            const currentEmailNorm = (user.email || '').trim().toLowerCase();

            const systemTasks = system.map((p) => ({
                prompt: p.key,
                _displayName: p.name || p.key,
                instruction: (p.instruction || '').trim(),
                is_custom: false,
                _source: 'system',
                _readOnly: !isAdmin,
            }));

            const ownTasks = users
                .filter(
                    (g) => (g.email || '').trim().toLowerCase() === currentEmailNorm
                )
                .flatMap((g) =>
                    (g.prompts || []).map((p) => ({
                        prompt: p.key,
                        _displayName: p.name || p.key,
                        instruction: (p.instruction || '').trim(),
                        active: typeof p.active === 'boolean' ? p.active : undefined,
                        warning_message:
                            p.warning_message !== undefined
                                ? p.warning_message
                                : undefined,
                        is_custom: true,
                        prompt_id: p.id,
                        _source: 'own',
                        _readOnly: false,
                    }))
                );

            const sharedTasks = users
                .filter(
                    (g) => (g.email || '').trim().toLowerCase() !== currentEmailNorm
                )
                .flatMap((g) => {
                    const displayName = (g.username || '').trim();
                    return (g.prompts || []).map((p) => ({
                        prompt: p.key,
                        _displayName: displayName
                            ? `${p.name || p.key} (${displayName})`
                            : p.name || p.key,
                        instruction: (p.instruction || '').trim(),
                        active: typeof p.active === 'boolean' ? p.active : undefined,
                        warning_message:
                            p.warning_message !== undefined
                                ? p.warning_message
                                : undefined,
                        is_custom: true,
                        prompt_id: p.id,
                        _source: 'shared',
                        _readOnly: true,
                        ownerEmail: g.email,
                        ownerUsername: displayName || null,
                    }));
                });

            const personaProcess = {
                process_name: Operation.PERSONA_PROMPTS,
                sub_tasks: [...systemTasks, ...sharedTasks, ...ownTasks],
            };

            const withUiIds = ensurePersonaUiIds([personaProcess]);
            setProcesses(withUiIds);
            setOriginalProcesses(JSON.parse(JSON.stringify(withUiIds)));
            setExpandPromptId(null);
        } catch (err) {
            console.error('Error fetching persona prompts:', err);
        }
    };

    useEffect(() => {
        fetchProcesses();
    }, [user?.email, repoid, isAdmin]);

    useEffect(() => {
        if (!scrollToPromptId) return;

        // Expand first so the prompt fills the view.
        setExpandPromptId(scrollToPromptId);

        const getScrollParent = (el) => {
            let node = el;
            while (node && node.parentElement) {
                node = node.parentElement;
                const style = window.getComputedStyle(node);
                const overflowY = style?.overflowY;
                if (overflowY === 'auto' || overflowY === 'scroll') {
                    return node;
                }
            }
            return window;
        };

        const doScroll = (behavior) => {
            const target = document.getElementById(scrollToPromptId);
            if (!target) return;

            const parent = getScrollParent(target);
            const offset = 0;

            if (parent === window) {
                const rect = target.getBoundingClientRect();
                const top = window.scrollY + rect.top - offset;
                window.scrollTo({ top, behavior });
                return;
            }

            const parentRect = parent.getBoundingClientRect();
            const rect = target.getBoundingClientRect();
            const top =
                parent.scrollTop + (rect.top - parentRect.top) - offset;
            parent.scrollTo({ top, behavior });
        };

        const scroll = () => {
            doScroll('smooth');
            window.setTimeout(() => {
                doScroll('smooth');
            }, 200);
            window.setTimeout(() => {
                doScroll('smooth');
                setScrollToPromptId(null);
            }, 450);
        };

        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(scroll);
        });
    }, [scrollToPromptId, processes]);

    const handleInstructionChange = (idx, newValue) => {
        setProcesses((prev) =>
            (prev || []).map((process) => {
                if (process.process_name === selectedProcess) {
                    return {
                        ...process,
                        sub_tasks: (process.sub_tasks || []).map((t, i) =>
                            i === idx ? { ...t, instruction: newValue } : t
                        ),
                    };
                }
                return process;
            })
        );
    };

    const handlePromptNameChange = (idx, newValue) => {
        setProcesses((prev) =>
            (prev || []).map((process) => {
                if (process.process_name === selectedProcess) {
                    return {
                        ...process,
                        sub_tasks: (process.sub_tasks || []).map((t, i) =>
                            i === idx ? { ...t, prompt: newValue } : t
                        ),
                    };
                }
                return process;
            })
        );
    };

    const addPersonaPromptToState = (promptName, promptInstruction) => {
        const uiId = `ui-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        setProcesses((prev) =>
            (prev || []).map((process) => {
                if (process.process_name !== Operation.PERSONA_PROMPTS) return process;
                const nextTask = {
                    prompt: promptName,
                    instruction: promptInstruction,
                    is_custom: true,
                    _isUnsaved: true,
                    _uiId: uiId,
                };
                return {
                    ...process,
                    sub_tasks: [...(process.sub_tasks || []), nextTask],
                };
            })
        );
        return uiId;
    };

    const handleAddPersonaPrompt = () => {
        // Build a base name like "Prompt (N)" where N is the next index,
        // and ensure uniqueness using the existing copy-name helper if needed.
        const existing = getPersonaPromptNames(processes);
        const nextIndex = existing.length + 1;
        const baseName = normalizePromptName(`Prompt (${nextIndex})`);

        let finalName = baseName;
        const hasCollision = existing.some(
            (n) => normalizePromptKey(n) === normalizePromptKey(baseName)
        );
        if (hasCollision) {
            finalName = buildCopyName(baseName, existing);
        }

        const uiId = addPersonaPromptToState(finalName, '');
        setScrollToPromptId(uiId);
    };

    const handleCopyPersonaPrompt = (idx) => {
        const persona = (processes || []).find(
            (p) => p?.process_name === Operation.PERSONA_PROMPTS
        );
        const tasks = persona?.sub_tasks || [];
        const src = tasks[idx];
        if (!src) return;

        const existing = getPersonaPromptNames(processes);
        const nextName = buildCopyName(src.prompt, existing);

        const uiId = `ui-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        const nextTask = {
            prompt: nextName,
            instruction: src.instruction ?? '',
            is_custom: true,
            _isUnsaved: true,
            _uiId: uiId,
        };

        setProcesses((prev) =>
            (prev || []).map((p) => {
                if (p.process_name !== Operation.PERSONA_PROMPTS) return p;
                const currentTasks = p.sub_tasks || [];
                const nextTasks = [...currentTasks];
                nextTasks.splice(idx + 1, 0, nextTask);
                return { ...p, sub_tasks: nextTasks };
            })
        );

        setScrollToPromptId(uiId);
    };

    const handleDeletePersonaPrompt = async (idx, task) => {
        const persona = (processes || []).find(
            (p) => p?.process_name === Operation.PERSONA_PROMPTS
        );
        const tasks = persona?.sub_tasks || [];
        const target = task || tasks[idx];
        if (!target) return;

        if (!target?._isUnsaved) {
            const promptId = target?.prompt_id;
            if (!promptId) {
                alert(AlertType.ERROR, 'Unable to delete this prompt. Missing prompt id.');
                return;
            }

            try {
                const url = getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/prompts_routes/persona-prompt-usage`
                );

                const resp = await axios.get(url, {
                    params: {
                        email: user?.email,
                        prompt_id: promptId,
                    },
                    headers: { 'Content-Type': 'application/json' },
                });

                const payload = resp?.data || {};
                if (resp?.status >= 200 && resp?.status < 300) {
                    const usedBy = payload?.used_by_repos || [];
                    if (usedBy.length > 0) {
                        alert(
                            AlertType.WARNING,
                            `This prompt is attached to ${usedBy.join(
                                ', '
                            )}'s virtual repository. Please remove the prompt from there to delete this prompt.`
                        );
                        return;
                    }
                }
            } catch (e) {
            }
        }

        setPendingPersonaDeleteTarget({ idx, task: target });
        setConfirmPersonaDeleteOpen(true);
    };

    const confirmDeletePersonaPrompt = () => {
        const payload = pendingPersonaDeleteTarget;
        if (!payload) {
            setConfirmPersonaDeleteOpen(false);
            return;
        }

        const { idx, task: target } = payload;

        if (!target?._isUnsaved) {
            const promptId = target?.prompt_id;
            if (!promptId) {
                alert(AlertType.ERROR, 'Unable to delete this prompt. Missing prompt id.');
                setConfirmPersonaDeleteOpen(false);
                setPendingPersonaDeleteTarget(null);
                return;
            }
            setPendingPersonaPromptDeletes((prev) =>
                prev.includes(promptId) ? prev : [...prev, promptId]
            );
        }

        setProcesses((prev) =>
            (prev || []).map((p) => {
                if (p.process_name !== Operation.PERSONA_PROMPTS) return p;
                return {
                    ...p,
                    sub_tasks: (p.sub_tasks || []).filter((t, i) => i !== idx),
                };
            })
        );

        setConfirmPersonaDeleteOpen(false);
        setPendingPersonaDeleteTarget(null);
    };

    const handlePromptSave = async () => {
        if (!hasChanges) return;

        if (hasDuplicatePersonaPrompts(processes)) {
            throw new Error('Duplicate persona prompt names are not allowed.');
        }
        if (hasInvalidPersonaPrompts(processes)) {
            throw new Error('Persona prompt name and instruction are required.');
        }

        if (pendingPersonaPromptDeletes.length > 0) {
            const url = getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/prompts_routes/delete-persona-prompt`
            );

            for (const promptId of pendingPersonaPromptDeletes) {
                let resp;
                try {
                    resp = await axios.delete(url, {
                        headers: { 'Content-Type': 'application/json' },
                        data: {
                            email: user?.email,
                            prompt_id: promptId,
                        },
                    });
                } catch (e) {
                    resp = e?.response;
                }

                const payload = resp?.data || {};

                if (!resp || resp.status < 200 || resp.status >= 300) {
                    const usedBy = payload?.used_by_repos || [];
                    if (resp?.status === 409 && usedBy.length > 0) {
                        alert(
                            AlertType.WARNING,
                            `This prompt is attached to ${usedBy.join(
                                ', '
                            )}'s virtual repository. Please remove the prompt from there to delete this prompt.`
                        );
                        throw new Error('Prompt is in use by a virtual repository.');
                    }
                    throw new Error(payload?.detail || 'Failed to delete prompt.');
                }
            }

            setPendingPersonaPromptDeletes([]);
        }

        // When saving, only send system + current user's own persona prompts.
        // Shared prompts from other users are read-only and should not be
        // included in the update payload.
        const processesToSave = (processes || []).map((p) => {
            if (p.process_name !== Operation.PERSONA_PROMPTS) return p;
            return {
                ...p,
                sub_tasks: (p.sub_tasks || []).filter(
                    (t) => t?._source !== 'shared'
                ),
            };
        });

        try {
            await axios.post(
                getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/prompts_routes/update-system-prompts`
                ),
                {
                    email: user?.email,
                    processes: processesToSave,
                },
                {
                    headers: { 'Content-Type': 'application/json' },
                }
            );
        } catch (e) {
            const data = e?.response?.data || {};
            throw new Error(data?.detail || data?.message || 'Failed to save prompts.');
        }

        await fetchProcesses();
    };

    if (isSavingPersonaPrompts) {
        return (
            <div className='w-full h-full flex justify-center items-center'>
                <LoadingView
                    loadingText={'Saving persona prompts...'}
                    inLine={false}
                />
            </div>
        );
    }

    return (
        <div className='w-full h-full flex flex-col'>
            <div className='px-3'>
                <div className='flex items-start justify-between gap-3'>
                    <div className='flex flex-col'>
                        <div className='text-texts-secondary300 font-semibold leading-tight'>
                            Persona Prompts
                        </div>
                        <div className='flex items-start space-x-2 text-xs text-texts-secondary300 mt-2'>
                            <FontAwesomeIcon
                                icon={faCircleInfo}
                                className='text-texts-secondary400'
                            />
                            <span>
                                Persona Prompts are reusable instruction templates. Create and manage them here, then pick one per virtual repo in the Configure Repository tab.
                            </span>
                        </div>
                    </div>
                    <div className='flex items-center gap-2'>
                        <Button
                            text='Add'
                            type='success'
                            icon={faPlus}
                            onClick={handleAddPersonaPrompt}
                            className='ml-2'
                        />
                        <Button
                            text='Save'
                            type='success'
                            icon={faFloppyDisk}
                            disabled={!hasChanges}
                            onClick={async () => {
                                try {
                                    setIsSavingPersonaPrompts(true);
                                    await handlePromptSave();
                                    alert(
                                        AlertType.SUCCESS,
                                        'Persona prompts saved.'
                                    );
                                    onSaved?.();
                                } catch (e) {
                                    alert(
                                        AlertType.ERROR,
                                        e?.message ||
                                            'Failed to save persona prompts'
                                    );
                                } finally {
                                    setIsSavingPersonaPrompts(false);
                                }
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className='px-3 py-2'>
                <hr className='border-backgrounds-border' />
            </div>

            <div className='flex-1 overflow-auto pt-2 pb-2'>
                <PromptListView
                    processes={processes}
                    currentProcess={currentProcess}
                    selectedProcess={selectedProcess}
                    selectedPrompt={selectedPrompt}
                    setSelectedPrompt={setSelectedPrompt}
                    isPersonaProcess={true}
                    isAdmin={!!isAdmin}
                    expandPromptId={expandPromptId}
                    getDuplicatePersonaKeys={getDuplicatePersonaKeys}
                    getPersonaPromptNames={getPersonaPromptNames}
                    normalizePromptKey={normalizePromptKey}
                    normalizePromptName={normalizePromptName}
                    promptDomId={promptDomId}
                    formatProcessName={formatProcessName}
                    handleInstructionChange={handleInstructionChange}
                    handlePromptNameChange={handlePromptNameChange}
                    handleCopyPersonaPrompt={handleCopyPersonaPrompt}
                    handleDeletePersonaPrompt={handleDeletePersonaPrompt}
                />
            </div>

            {confirmPersonaDeleteOpen && (
                <ConfirmModal
                    modalText='Are you sure you want to delete this persona prompt? Click Save to apply the deletion.'
                    onConfirm={() => {
                        confirmDeletePersonaPrompt();
                    }}
                    onCancel={() => {
                        setConfirmPersonaDeleteOpen(false);
                        setPendingPersonaDeleteTarget(null);
                    }}
                />
            )}

        </div>
    );
};
